import { useCallback, useEffect, useRef, useState } from "react";
import { Plus, Sparkles, Pencil, Trash2, Copy, ArrowUp, ArrowDown, ChevronRight, ListChecks, X } from "lucide-react";
import { theme, card, emptyFrame, floatingSheet, btnPrimary, btnSecondary, inputStyle } from "../theme";
import { api, apiErrorMessage } from "../api/client";

// Supplier questionnaire library for the TPRM page (docs/TPRM-QUESTIONNAIRES.md):
// the platform's built-in tier sets, the organisation's own questionnaires and
// the AI drafting flow. Shapes mirror backend/src/lib/tprmScoring.ts and
// lib/tprmQuestionnaireTemplates.ts — the server validates everything again.

// ── Types ─────────────────────────────────────────────────────────────────────

export type TprmAdverseAnswer = "yes" | "no";

export interface TprmQuestion {
  text:          string;
  adverseAnswer: TprmAdverseAnswer;   // the answer that counts against the supplier
  guidance?:     string;
  category?:     string;
  // Answered with a list of the supplier's subcontractors rather than Yes / No; never scored.
  answerType?:   "subcontractors";
}

/** Mirror of SUBCONTRACTOR_LIST_QUESTION in backend/src/lib/tprmScoring.ts. */
export const SUBCONTRACTOR_LIST_QUESTION: TprmQuestion = {
  text: "List the subcontractors and sub-processors that support the service you provide to us: their name, what they do for you and the country they work from.",
  adverseAnswer: "no",
  answerType: "subcontractors",
  guidance: "Include hosting and cloud providers, support and development partners, and anyone who can access our data. Leave the list empty if there are none.",
};

export const isListQuestion = (q: { answerType?: string }) => q.answerType === "subcontractors";

export interface BuiltinQuestionSet {
  key:         string;                // "builtin:<tier>"
  tier:        string;
  name:        string;
  description: string;
  questions:   TprmQuestion[];
}

export interface QuestionnaireTemplate {
  id:          string;
  name:        string;
  description: string | null;
  source:      string;                // custom | builtin | ai
  riskTier:    string | null;
  questions:   TprmQuestion[];
  createdAt:   string;
  updatedAt:   string;
}

export interface QuestionnaireCatalogue {
  builtin:   BuiltinQuestionSet[];
  templates: QuestionnaireTemplate[];
}

export const EMPTY_CATALOGUE: QuestionnaireCatalogue = { builtin: [], templates: [] };

export const MAX_TEMPLATE_QUESTIONS = 60;
const MIN_DRAFT_QUESTIONS = 5;
const MAX_DRAFT_QUESTIONS = 40;
const RISK_TIERS = ["low", "medium", "high", "critical"];

export function useQuestionnaireCatalogue(enabled: boolean) {
  const [catalogue, setCatalogue] = useState<QuestionnaireCatalogue>(EMPTY_CATALOGUE);
  const [loading,   setLoading]   = useState(true);
  const reload = useCallback(async () => {
    try {
      setCatalogue(await api.get<QuestionnaireCatalogue>("/tprm/questionnaire-templates"));
    } catch { /* add-on off or no permission — pickers show nothing */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { if (enabled) reload(); }, [enabled, reload]);
  return { catalogue, loading, reload };
}

export interface ResolvedQuestionSet {
  key:         string;
  name:        string;
  questions:   TprmQuestion[];
  templateId:  string | null;
  builtinTier: string | null;
}

/** "builtin:<tier>" or a template id → the set it names, or null if it no longer exists. */
export function findQuestionSet(catalogue: QuestionnaireCatalogue, key: string): ResolvedQuestionSet | null {
  const builtin = catalogue.builtin.find((b) => b.key === key);
  if (builtin) return { key, name: builtin.name, questions: builtin.questions, templateId: null, builtinTier: builtin.tier };
  const template = catalogue.templates.find((t) => t.id === key);
  if (template) return { key, name: template.name, questions: template.questions, templateId: template.id, builtinTier: null };
  return null;
}

export const SOURCE_LABELS: Record<string, string> = {
  custom:  "Custom",
  builtin: "From standard",
  ai:      "AI draft",
};

// ── Small pieces ──────────────────────────────────────────────────────────────

function SourceBadge({ source }: { source: string }) {
  const colours: Record<string, { bg: string; text: string }> = {
    ai:      { bg: "var(--c-accentBg)",  text: "var(--c-accentText)" },
    builtin: { bg: "var(--c-faint)",     text: "var(--c-muted)" },
    custom:  { bg: "var(--c-successBg)", text: "var(--c-successText)" },
  };
  const c = colours[source] ?? colours.custom;
  return (
    <span style={{ display: "inline-block", padding: "2px 10px", borderRadius: 3, fontSize: 11, fontWeight: 600, background: c.bg, color: c.text, whiteSpace: "nowrap" }}>
      {SOURCE_LABELS[source] ?? source}
    </span>
  );
}

function QuestionPreview({ questions }: { questions: TprmQuestion[] }) {
  return (
    <ol style={{ margin: "10px 0 0", paddingLeft: 22, display: "flex", flexDirection: "column", gap: 6 }}>
      {questions.map((q, i) => (
        <li key={i} style={{ fontSize: 12, color: theme.color.text, lineHeight: 1.45 }}>
          {q.text}
          {isListQuestion(q) && <span style={{ marginLeft: 6, fontSize: 10, color: theme.color.muted, fontWeight: 600 }}>LIST, NOT SCORED</span>}
          {!isListQuestion(q) && q.adverseAnswer === "yes" && <span style={{ marginLeft: 6, fontSize: 10, color: "var(--c-warningText)", fontWeight: 600 }}>YES = RISK FLAG</span>}
          {q.guidance && <div style={{ fontSize: 11, color: theme.color.muted, marginTop: 2 }}>{q.guidance}</div>}
        </li>
      ))}
    </ol>
  );
}

const smallBtn: React.CSSProperties = {
  fontSize: 12, padding: "5px 12px", borderRadius: 3, border: "1px solid var(--c-border)",
  background: "var(--c-panel)", color: theme.color.text, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5,
};

const iconBtn: React.CSSProperties = {
  background: "none", border: "1px solid var(--c-border)", borderRadius: 3, color: theme.color.muted,
  cursor: "pointer", padding: 4, display: "inline-flex", alignItems: "center", justifyContent: "center",
};

const textareaStyle: React.CSSProperties = { ...inputStyle, resize: "vertical", fontFamily: "inherit" };
const labelStyle: React.CSSProperties = { display: "flex", flexDirection: "column", gap: 5, fontSize: 13 };
const labelText:  React.CSSProperties = { fontWeight: 500, color: theme.color.text };

// ── Editor ────────────────────────────────────────────────────────────────────

interface EditorQuestion extends TprmQuestion { key: number; }

interface EditorState {
  id:          string | null;     // set when editing an existing template
  name:        string;
  description: string;
  riskTier:    string;            // "" = any tier
  questions:   EditorQuestion[];
  basedOnTier?: string;           // provenance for a new template cloned from a built-in set
  draftRunId?:  string;           // provenance for a new template saved from an AI draft
  banner?:      string;           // one line shown above the form (e.g. "Review the AI draft")
}

let questionKeySeq = 1;
const withKeys = (questions: TprmQuestion[]): EditorQuestion[] => questions.map((q) => ({ ...q, key: questionKeySeq++ }));
const blankQuestion = (): EditorQuestion => ({ key: questionKeySeq++, text: "", adverseAnswer: "no" });

interface TemplateEditorProps {
  initial:  EditorState;
  onSaved:  () => Promise<void> | void;
  onCancel: () => void;
}

function TemplateEditor({ initial, onSaved, onCancel }: TemplateEditorProps) {
  const [state,  setState]  = useState<EditorState>(initial);
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState<string | null>(null);

  function updateQuestion(key: number, patch: Partial<TprmQuestion>) {
    setState((s) => ({ ...s, questions: s.questions.map((q) => (q.key === key ? { ...q, ...patch } : q)) }));
  }
  function move(key: number, delta: -1 | 1) {
    setState((s) => {
      const idx = s.questions.findIndex((q) => q.key === key);
      const to  = idx + delta;
      if (idx < 0 || to < 0 || to >= s.questions.length) return s;
      const next = [...s.questions];
      [next[idx], next[to]] = [next[to], next[idx]];
      return { ...s, questions: next };
    });
  }
  function remove(key: number) {
    setState((s) => ({ ...s, questions: s.questions.filter((q) => q.key !== key) }));
  }
  function add() {
    setState((s) => (s.questions.length >= MAX_TEMPLATE_QUESTIONS ? s : { ...s, questions: [...s.questions, blankQuestion()] }));
  }

  function addListQuestion() {
    setState((s) => (s.questions.length >= MAX_TEMPLATE_QUESTIONS || s.questions.some(isListQuestion)
      ? s
      : { ...s, questions: [...s.questions, { ...SUBCONTRACTOR_LIST_QUESTION, key: questionKeySeq++ }] }));
  }

  function validate(): string | null {
    if (!state.name.trim()) return "Give the questionnaire a name.";
    // A question emptied by mistake is named rather than dropped with its guidance (DOC-43).
    const blank = state.questions.findIndex((q) => !q.text.trim());
    if (blank >= 0) return `Question ${blank + 1} has no text. Write the question or remove it.`;
    const questions = state.questions;
    if (questions.length === 0) return "Add at least one question.";
    const seen = new Set<string>();
    for (const q of questions) {
      const key = q.text.trim().toLowerCase();
      if (seen.has(key)) return `Duplicate question: "${q.text.trim()}"`;
      seen.add(key);
    }
    return null;
  }

  async function save() {
    const problem = validate();
    if (problem) { setError(problem); return; }
    setSaving(true);
    setError(null);
    const questions = state.questions
      .map((q) => ({
        text:          q.text.trim(),
        adverseAnswer: q.adverseAnswer,
        ...(q.guidance?.trim() ? { guidance: q.guidance.trim() } : {}),
        ...(q.category?.trim() ? { category: q.category.trim() } : {}),
        // The list question keeps its type, so a copy never turns it into a scored Yes / No (DOC-28).
        ...(isListQuestion(q) ? { answerType: "subcontractors" as const } : {}),
      }));
    const body = {
      name:        state.name.trim(),
      description: state.description.trim() || null,
      riskTier:    state.riskTier || null,
      questions,
    };
    try {
      if (state.id) {
        await api.put(`/tprm/questionnaire-templates/${state.id}`, body);
      } else {
        await api.post("/tprm/questionnaire-templates", {
          ...body,
          ...(state.basedOnTier ? { basedOnTier: state.basedOnTier } : {}),
          ...(state.draftRunId  ? { draftRunId:  state.draftRunId }  : {}),
        });
      }
      await onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save the questionnaire");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={card}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: theme.color.heading }}>
            {state.id ? "Edit questionnaire" : "New questionnaire"}
          </div>
          {state.banner && <div style={{ fontSize: 12, color: "var(--c-accentText)", marginTop: 4 }}>{state.banner}</div>}
        </div>
        <button onClick={onCancel} style={{ background: "none", border: "none", color: theme.color.muted, cursor: "pointer", padding: 4 }} aria-label="Close editor">
          <X size={18} />
        </button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 14, marginBottom: 14 }}>
        <label style={labelStyle}>
          <span style={labelText}>Name *</span>
          <input type="text" value={state.name} maxLength={120} onChange={(e) => setState((s) => ({ ...s, name: e.target.value }))}
                 placeholder="e.g. Cloud hosting providers — critical" style={inputStyle} />
        </label>
        <label style={labelStyle}>
          <span style={labelText}>Suggested for</span>
          <select value={state.riskTier} onChange={(e) => setState((s) => ({ ...s, riskTier: e.target.value }))} style={inputStyle}>
            <option value="">Any risk tier</option>
            {RISK_TIERS.map((t) => <option key={t} value={t}>{t[0].toUpperCase() + t.slice(1)} risk suppliers</option>)}
          </select>
        </label>
        <label style={{ ...labelStyle, gridColumn: "1 / -1" }}>
          <span style={labelText}>Description</span>
          <textarea value={state.description} maxLength={1000} rows={2} onChange={(e) => setState((s) => ({ ...s, description: e.target.value }))}
                    placeholder="What it covers and which suppliers it is for" style={textareaStyle} />
        </label>
      </div>

      <div style={{ fontSize: 12, fontWeight: 600, color: theme.color.muted, marginBottom: 8 }}>
        Questions ({state.questions.length}/{MAX_TEMPLATE_QUESTIONS}) — suppliers answer each with Yes, No, Partial or N/A{state.questions.some(isListQuestion) ? ", except the subcontractor list" : ""}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 12 }}>
        {state.questions.map((q, idx) => (
          <div key={q.key} style={{ border: "1px solid var(--c-border)", borderRadius: 3, padding: "12px 14px", background: "var(--c-panelAlt)" }}>
            <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
              <span style={{ fontSize: 12, color: theme.color.faint, marginTop: 9, minWidth: 22 }}>{idx + 1}.</span>
              <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
                <textarea
                  value={q.text} maxLength={500} rows={2}
                  onChange={(e) => updateQuestion(q.key, { text: e.target.value })}
                  placeholder="Does the supplier …?"
                  style={textareaStyle}
                />
                <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 8 }}>
                  <input type="text" value={q.category ?? ""} maxLength={60} onChange={(e) => updateQuestion(q.key, { category: e.target.value })}
                         placeholder="Category (optional)" style={{ ...inputStyle, fontSize: 12, padding: "7px 10px" }} />
                  <input type="text" value={q.guidance ?? ""} maxLength={600} onChange={(e) => updateQuestion(q.key, { guidance: e.target.value })}
                         placeholder="Guidance shown to the respondent (optional)" style={{ ...inputStyle, fontSize: 12, padding: "7px 10px" }} />
                </div>
                {isListQuestion(q) ? (
                  <div style={{ fontSize: 11, color: theme.color.muted }}>
                    The supplier answers with a list of its subcontractors, which fills its fourth-party register. Never scored.
                  </div>
                ) : (
                <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 11, color: theme.color.muted }}>Answer that counts against the supplier:</span>
                  {(["no", "yes"] as TprmAdverseAnswer[]).map((opt) => {
                    const active = q.adverseAnswer === opt;
                    return (
                      <button key={opt} type="button" onClick={() => updateQuestion(q.key, { adverseAnswer: opt })} style={{
                        padding: "3px 10px", borderRadius: 3, fontSize: 11, fontWeight: active ? 600 : 400,
                        border: active ? `2px solid ${theme.color.accent}` : "1px solid var(--c-border)",
                        background: active ? "var(--c-accentBg)" : "var(--c-panel)",
                        color: active ? "var(--c-accentText)" : theme.color.muted, cursor: "pointer",
                      }}>
                        {opt === "no" ? "No (Yes is the good answer)" : "Yes (e.g. \"have you had a breach?\")"}
                      </button>
                    );
                  })}
                </div>
                )}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <button type="button" onClick={() => move(q.key, -1)} disabled={idx === 0} style={{ ...iconBtn, opacity: idx === 0 ? 0.4 : 1 }} aria-label="Move up"><ArrowUp size={13} /></button>
                <button type="button" onClick={() => move(q.key, 1)} disabled={idx === state.questions.length - 1} style={{ ...iconBtn, opacity: idx === state.questions.length - 1 ? 0.4 : 1 }} aria-label="Move down"><ArrowDown size={13} /></button>
                <button type="button" onClick={() => remove(q.key)} style={{ ...iconBtn, color: "var(--c-dangerText)" }} aria-label="Remove question"><Trash2 size={13} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <button type="button" onClick={add} disabled={state.questions.length >= MAX_TEMPLATE_QUESTIONS} style={{ ...smallBtn, marginBottom: 16, opacity: state.questions.length >= MAX_TEMPLATE_QUESTIONS ? 0.5 : 1 }}>
        <Plus size={13} /> Add question
      </button>
      {!state.questions.some(isListQuestion) && (
        <button type="button" onClick={addListQuestion} disabled={state.questions.length >= MAX_TEMPLATE_QUESTIONS} style={{ ...smallBtn, marginBottom: 16, marginLeft: 8, opacity: state.questions.length >= MAX_TEMPLATE_QUESTIONS ? 0.5 : 1 }}>
          <Plus size={13} /> Add the subcontractor list question
        </button>
      )}

      {error && (
        <div style={{ marginBottom: 12, padding: "10px 14px", borderRadius: 3, background: "var(--c-dangerBg)", color: "var(--c-dangerText)", fontSize: 12 }}>
          {error}
        </div>
      )}

      <div style={{ display: "flex", gap: 10 }}>
        <button type="button" onClick={save} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.6 : 1, cursor: saving ? "not-allowed" : "pointer" }}>
          {saving ? "Saving…" : state.id ? "Save changes" : "Save questionnaire"}
        </button>
        <button type="button" onClick={onCancel} style={btnSecondary}>Cancel</button>
      </div>
    </div>
  );
}

// ── AI draft ──────────────────────────────────────────────────────────────────

interface DraftResult { name: string; description: string; questions: TprmQuestion[]; }

interface DraftRun {
  runId:        string | null;
  status:       "queued" | "running" | "done" | "error" | null;
  result?:      DraftResult | null;
  errorMessage?: string | null;
  completedAt?: string | null;
}

export interface SupplierOption { id: string; name: string; riskTier: string; }

interface AiDraftDialogProps {
  suppliers:       SupplierOption[];
  defaultCounts:   Record<string, number>;
  running:         boolean;
  error:           string | null;
  onStart:         (input: { supplierId?: string; riskTier: string; questionCount: number; focus?: string }) => Promise<void>;
  onClose:         () => void;
}

function AiDraftDialog({ suppliers, defaultCounts, running, error, onStart, onClose }: AiDraftDialogProps) {
  const [supplierId, setSupplierId] = useState("");
  const [riskTier,   setRiskTier]   = useState("medium");
  const [count,      setCount]      = useState(defaultCounts.medium ?? 8);
  const [focus,      setFocus]      = useState("");
  // Starting the draft is a request of its own: a second click while it is outstanding sent a second POST (DOC-20).
  const [starting,   setStarting]   = useState(false);
  const busy = running || starting;
  async function start() {
    if (busy) return;
    setStarting(true);
    try { await onStart({ supplierId: supplierId || undefined, riskTier, questionCount: count, focus: focus.trim() || undefined }); }
    finally { setStarting(false); }
  }

  function pickSupplier(id: string) {
    setSupplierId(id);
    const supplier = suppliers.find((s) => s.id === id);
    if (supplier && RISK_TIERS.includes(supplier.riskTier)) {
      setRiskTier(supplier.riskTier);
      setCount(defaultCounts[supplier.riskTier] ?? count);
    }
  }
  function pickTier(tier: string) {
    setRiskTier(tier);
    setCount(defaultCounts[tier] ?? count);
  }

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 300, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ ...floatingSheet, padding: 28, width: 520, maxWidth: "100%" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, paddingBottom: 10, borderBottom: "1px solid var(--c-line)" }}>
          <Sparkles size={16} color="var(--c-accentText)" />
          <div style={{ fontSize: 15, fontWeight: 700, color: theme.color.heading }}>Draft a questionnaire with AI</div>
        </div>
        <p style={{ fontSize: 12, color: theme.color.subtle, lineHeight: 1.5, margin: "0 0 16px" }}>
          The draft uses your organisation profile, enrolled standards and regulations, the supplier you pick and the
          platform's standard set for the tier. You review and edit it before it is saved — nothing is sent to a supplier.
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 16 }}>
          <label style={labelStyle}>
            <span style={labelText}>Supplier</span>
            <select value={supplierId} onChange={(e) => pickSupplier(e.target.value)} style={inputStyle} disabled={running}>
              <option value="">No specific supplier — a reusable questionnaire</option>
              {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.riskTier})</option>)}
            </select>
          </label>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <label style={labelStyle}>
              <span style={labelText}>Risk tier</span>
              <select value={riskTier} onChange={(e) => pickTier(e.target.value)} style={inputStyle} disabled={running}>
                {RISK_TIERS.map((t) => <option key={t} value={t}>{t[0].toUpperCase() + t.slice(1)}</option>)}
              </select>
            </label>
            <label style={labelStyle}>
              <span style={labelText}>Number of questions ({MIN_DRAFT_QUESTIONS}–{MAX_DRAFT_QUESTIONS})</span>
              <input type="number" min={MIN_DRAFT_QUESTIONS} max={MAX_DRAFT_QUESTIONS} value={count} disabled={running}
                     onChange={(e) => setCount(Math.max(MIN_DRAFT_QUESTIONS, Math.min(MAX_DRAFT_QUESTIONS, Number(e.target.value) || MIN_DRAFT_QUESTIONS)))}
                     style={inputStyle} />
            </label>
          </div>
          <label style={labelStyle}>
            <span style={labelText}>Focus (optional)</span>
            <textarea value={focus} maxLength={2000} rows={3} disabled={running} onChange={(e) => setFocus(e.target.value)}
                      placeholder="e.g. They host our production database and process cardholder data — concentrate on encryption, subprocessors, incident notification and exit planning."
                      style={textareaStyle} />
          </label>
        </div>

        {error && (
          <div style={{ marginBottom: 12, padding: "10px 14px", borderRadius: 3, background: "var(--c-dangerBg)", color: "var(--c-dangerText)", fontSize: 12 }}>
            {error}
          </div>
        )}

        {running ? (
          <div style={{ fontSize: 12, color: theme.color.muted, marginBottom: 14 }}>
            Drafting… this usually takes under a minute. You can close this window; the draft opens for review when it is ready.
          </div>
        ) : null}

        <div style={{ display: "flex", gap: 10 }}>
          <button type="button" disabled={busy} style={{ ...btnPrimary, display: "flex", alignItems: "center", gap: 6, opacity: busy ? 0.6 : 1, cursor: busy ? "not-allowed" : "pointer" }}
                  onClick={start}>
            <Sparkles size={14} /> {starting ? "Starting…" : running ? "Drafting…" : "Generate draft"}
          </button>
          <button type="button" onClick={onClose} style={btnSecondary}>{running ? "Close" : "Cancel"}</button>
        </div>
      </div>
    </div>
  );
}

// ── Tab ───────────────────────────────────────────────────────────────────────

interface QuestionnaireTemplatesTabProps {
  catalogue: QuestionnaireCatalogue;
  loading:   boolean;
  suppliers: SupplierOption[];
  onChanged: () => Promise<void> | void;
  /** tprm:write. Without it the sets are read-only: the server refuses every write anyway. */
  canWrite: boolean;
}

export function QuestionnaireTemplatesTab({ catalogue, loading, suppliers, onChanged, canWrite }: QuestionnaireTemplatesTabProps) {
  const [editor,      setEditor]      = useState<EditorState | null>(null);
  const [expanded,    setExpanded]    = useState<string | null>(null);
  const [error,       setError]       = useState<string | null>(null);
  const [aiOpen,      setAiOpen]      = useState(false);
  const [aiError,     setAiError]     = useState<string | null>(null);
  const [draftRunId,  setDraftRunId]  = useState<string | null>(null);
  const [draftReady,  setDraftReady]  = useState<{ runId: string; result: DraftResult } | null>(null);
  const pollRef      = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollStartRef = useRef(0);
  // Read inside the poll callback, which outlives the render it was created in.
  const editorOpenRef = useRef(false);
  editorOpenRef.current = !!editor;

  const defaultCounts = Object.fromEntries(catalogue.builtin.map((b) => [b.tier, b.questions.length]));

  function stopPolling() {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = null;
  }

  function openDraftForReview(runId: string, result: DraftResult) {
    setDraftReady(null);
    setAiOpen(false);
    setEditor({
      id:          null,
      name:        result.name,
      description: result.description ?? "",
      riskTier:    "",
      questions:   withKeys(result.questions),
      draftRunId:  runId,
      banner:      "AI draft — review every question, then save. Nothing is sent to a supplier until you choose to.",
    });
  }

  // Poll the run until it settles. Resumes an in-flight run found on mount too.
  useEffect(() => {
    if (!draftRunId) return;
    stopPolling();
    pollStartRef.current = Date.now();
    let failures = 0;
    pollRef.current = setInterval(async () => {
      if (Date.now() - pollStartRef.current > 5 * 60 * 1000) {
        stopPolling();
        setDraftRunId(null);
        setAiError("The AI draft timed out — the worker may be busy. Please try again.");
        return;
      }
      try {
        const run = await api.get<DraftRun>(`/tprm/questionnaire-templates/ai-draft/${draftRunId}`);
        failures = 0;
        if (run.status === "done" && run.result) {
          stopPolling();
          setDraftRunId(null);
          // Never replace a questionnaire someone is mid-way through editing.
          if (editorOpenRef.current) setDraftReady({ runId: draftRunId, result: run.result });
          else openDraftForReview(draftRunId, run.result);
        } else if (run.status === "error") {
          stopPolling();
          setDraftRunId(null);
          setAiError(run.errorMessage ?? "The AI draft failed");
          setAiOpen(true);
        }
      } catch {
        if (++failures >= 5) {
          stopPolling();
          setDraftRunId(null);
          setAiError("Lost connection while waiting for the AI draft. Please try again.");
        }
      }
    }, 3000);
    return stopPolling;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftRunId]);

  // A draft started before navigating away: pick it up again.
  useEffect(() => {
    api.get<DraftRun>("/tprm/questionnaire-templates/ai-draft/latest")
      .then((run) => {
        if (!run.runId) return;
        if (run.status === "queued" || run.status === "running") setDraftRunId(run.runId);
        else if (run.status === "done" && run.result && run.completedAt && Date.now() - new Date(run.completedAt).getTime() < 30 * 60 * 1000) {
          setDraftReady({ runId: run.runId, result: run.result });
        }
      })
      .catch(() => {});
  }, []);

  async function startDraft(input: { supplierId?: string; riskTier: string; questionCount: number; focus?: string }) {
    setAiError(null);
    try {
      const { runId } = await api.post<{ runId: string }>("/tprm/questionnaire-templates/ai-draft", input);
      setDraftRunId(runId);
    } catch (e) {
      setAiError(apiErrorMessage(e, "Could not start the AI draft"));
    }
  }

  function startBlank() {
    setEditor({ id: null, name: "", description: "", riskTier: "", questions: [blankQuestion()] });
  }
  function customiseBuiltin(set: BuiltinQuestionSet) {
    setEditor({
      id:          null,
      name:        `${set.name.replace(/\s*\(\d+ questions\)$/, "")} — customised`,
      description: "",
      riskTier:    set.tier,
      questions:   withKeys(set.questions),
      basedOnTier: set.tier,
      banner:      "Started from the platform's standard set — the original stays available unchanged.",
    });
  }
  function edit(t: QuestionnaireTemplate) {
    setEditor({ id: t.id, name: t.name, description: t.description ?? "", riskTier: t.riskTier ?? "", questions: withKeys(t.questions) });
  }
  function duplicate(t: QuestionnaireTemplate) {
    setEditor({ id: null, name: `${t.name} (copy)`, description: t.description ?? "", riskTier: t.riskTier ?? "", questions: withKeys(t.questions) });
  }
  async function remove(t: QuestionnaireTemplate) {
    if (!confirm(`Delete "${t.name}"? Questionnaires already sent from it keep their questions.`)) return;
    setError(null);
    try {
      await api.del(`/tprm/questionnaire-templates/${t.id}`);
      await onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not delete the questionnaire");
    }
  }

  if (editor) {
    return (
      <TemplateEditor
        initial={editor}
        onSaved={async () => { setEditor(null); await onChanged(); }}
        onCancel={() => setEditor(null)}
      />
    );
  }

  return (
    <div>
      {aiOpen && (
        <AiDraftDialog
          suppliers={suppliers}
          defaultCounts={defaultCounts}
          running={!!draftRunId}
          error={aiError}
          onStart={startDraft}
          onClose={() => { setAiOpen(false); setAiError(null); }}
        />
      )}

      <div style={{ display: "flex", gap: 12, alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", marginBottom: 20 }}>
        <p style={{ fontSize: 13, color: theme.color.subtle, margin: 0, maxWidth: 640, lineHeight: 1.6 }}>
          Build the questionnaires you send to suppliers: write your own, start from one of the platform's standard tier
          sets, or let the AI draft one from your organisation's context. Pick the questionnaire per supplier when you
          send an assessment; every questionnaire keeps a copy of its questions, so editing a set later never changes
          what a supplier already received.
        </p>
        {canWrite && <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button onClick={() => { setAiError(null); setAiOpen(true); }} style={{ ...btnSecondary, display: "flex", alignItems: "center", gap: 6, color: "var(--c-accentText)", borderColor: "var(--c-accent)" }}>
            <Sparkles size={14} /> Draft with AI
          </button>
          <button onClick={startBlank} style={{ ...btnPrimary, display: "flex", alignItems: "center", gap: 6 }}>
            <Plus size={14} /> New questionnaire
          </button>
        </div>}
      </div>

      {draftReady && (
        <div style={{ ...card, marginBottom: 16, padding: "12px 16px", display: "flex", alignItems: "center", gap: 12, background: "var(--c-accentBg)", border: "1px solid var(--c-accent)" }}>
          <Sparkles size={16} color="var(--c-accentText)" />
          <div style={{ flex: 1, fontSize: 13, color: "var(--c-accentText)" }}>
            An AI draft is ready to review: <strong>{draftReady.result.name}</strong> ({draftReady.result.questions.length} questions)
          </div>
          <button onClick={() => openDraftForReview(draftReady.runId, draftReady.result)} style={{ ...btnPrimary, padding: "6px 14px", fontSize: 12 }}>Review</button>
          <button onClick={() => setDraftReady(null)} style={{ ...btnSecondary, padding: "6px 14px", fontSize: 12 }}>Dismiss</button>
        </div>
      )}

      {!draftReady && draftRunId && !aiOpen && (
        <div style={{ ...card, marginBottom: 16, padding: "12px 16px", fontSize: 13, color: theme.color.muted }}>
          AI draft in progress — it opens for review when ready.
        </div>
      )}

      {error && (
        <div style={{ ...card, marginBottom: 16, padding: "10px 14px", background: "var(--c-dangerBg)", border: "1px solid var(--c-danger)", color: "var(--c-dangerText)", fontSize: 13 }}>
          {error}
        </div>
      )}

      {/* Organisation's own questionnaires */}
      <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading, marginBottom: 10 }}>Your questionnaires</div>
      {loading ? (
        <div style={{ ...card, color: theme.color.subtle, fontSize: 13, marginBottom: 24 }}>Loading…</div>
      ) : catalogue.templates.length === 0 ? (
        <div style={{ ...emptyFrame, padding: "28px", marginBottom: 24 }}>
          <ListChecks size={30} color="var(--c-muted)" style={{ marginBottom: 10 }} />
          <div style={{ fontSize: 14, fontWeight: 600, color: theme.color.heading, marginBottom: 6 }}>No questionnaires of your own yet</div>
          <div style={{ fontSize: 13, color: theme.color.muted, lineHeight: 1.6 }}>
            The standard sets below are used until you add one. Customise a standard set, write your own or draft one with AI.
          </div>
        </div>
      ) : (
        <div style={{ ...card, padding: 0, overflow: "hidden", marginBottom: 24 }}>
          {catalogue.templates.map((t, i) => (
            <div key={t.id} style={{ padding: "14px 16px", borderBottom: i < catalogue.templates.length - 1 ? "1px solid var(--c-border)" : "none" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading }}>{t.name}</span>
                    <SourceBadge source={t.source} />
                    <span style={{ fontSize: 11, color: theme.color.muted }}>
                      {t.questions.length} question{t.questions.length === 1 ? "" : "s"}
                      {t.riskTier ? ` · suggested for ${t.riskTier} risk` : ""}
                      {` · updated ${new Date(t.updatedAt).toLocaleDateString("en-GB")}`}
                    </span>
                  </div>
                  {t.description && <div style={{ fontSize: 12, color: theme.color.subtle, marginTop: 3, lineHeight: 1.5 }}>{t.description}</div>}
                </div>
                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  {canWrite && <>
                    <button onClick={() => edit(t)} style={smallBtn}><Pencil size={12} /> Edit</button>
                    <button onClick={() => duplicate(t)} style={smallBtn}><Copy size={12} /> Duplicate</button>
                    <button onClick={() => remove(t)} style={{ ...smallBtn, color: "var(--c-dangerText)", border: "none", background: "transparent" }}><Trash2 size={12} /> Delete</button>
                  </>}
                  <button onClick={() => setExpanded(expanded === t.id ? null : t.id)} style={{ background: "none", border: "none", color: theme.color.muted, cursor: "pointer", padding: 4 }} aria-label="Show questions">
                    <ChevronRight size={16} style={{ transform: expanded === t.id ? "rotate(90deg)" : "none", transition: "transform 0.15s" }} />
                  </button>
                </div>
              </div>
              {expanded === t.id && <QuestionPreview questions={t.questions} />}
            </div>
          ))}
        </div>
      )}

      {/* Built-in sets */}
      <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading, marginBottom: 4 }}>Standard questionnaires</div>
      <div style={{ fontSize: 12, color: theme.color.subtle, marginBottom: 10, lineHeight: 1.5 }}>
        Built into the platform, one per risk tier, and used by default for a supplier of that tier. They cannot be edited in place — customise a copy instead.
      </div>
      <div style={{ ...card, padding: 0, overflow: "hidden" }}>
        {catalogue.builtin.map((b, i) => (
          <div key={b.key} style={{ padding: "14px 16px", borderBottom: i < catalogue.builtin.length - 1 ? "1px solid var(--c-border)" : "none" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading }}>{b.name}</div>
                <div style={{ fontSize: 12, color: theme.color.subtle, marginTop: 3, lineHeight: 1.5 }}>{b.description}</div>
              </div>
              <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                {canWrite && <button onClick={() => customiseBuiltin(b)} style={smallBtn}><Copy size={12} /> Customise a copy</button>}
                <button onClick={() => setExpanded(expanded === b.key ? null : b.key)} style={{ background: "none", border: "none", color: theme.color.muted, cursor: "pointer", padding: 4 }} aria-label="Show questions">
                  <ChevronRight size={16} style={{ transform: expanded === b.key ? "rotate(90deg)" : "none", transition: "transform 0.15s" }} />
                </button>
              </div>
            </div>
            {expanded === b.key && <QuestionPreview questions={b.questions} />}
          </div>
        ))}
      </div>
    </div>
  );
}
